import { cn } from "@/lib/utils";
import { forwardRef } from "react";
import { cva } from "class-variance-authority";

// ============================================
// 타입 정의
// ============================================

interface ChipIconProps {
  size?: "24px" | "22px" | "20px" | "18px" | "16px";
  className?: string;
  children?: React.ReactNode;
}

// ============================================
// 스타일 (CVA)
// ============================================

const chipIconContainerVariants = cva(
  "inline-flex items-center justify-center rounded-[2px]",
  {
    variants: {
      size: {
        "24px": "w-6 h-6 rounded-[4px]",
        "22px": "w-[22px] h-[22px] rounded-[4px]",
        "20px": "w-[20px] h-[20px] rounded-[4px]",
        "18px": "w-[18px] h-[18px] rounded-[4px]",
        "16px": "w-[16px] h-[16px] rounded-[3.5556px]",
      },
    },
    defaultVariants: {
      size: "24px",
    },
  },
);

/**
 * 아이콘 두께는 --lucide-stroke-width 로만 조정한다. stroke-[...] 유틸로 덮으면
 * global.css 의 Chrome 151 보정 배수를 건너뛰어 이 아이콘만 얇게 남는다.
 * 기본값(1.2px)이면 아무것도 안 써도 된다.
 */
const chipIconInnerVariants = cva(
  "flex items-center justify-center [&>svg]:size-full",
  {
    variants: {
      size: {
        "24px": "size-4",
        "22px": "size-[14px]",
        "20px": "size-3",
        "18px": "size-[10px]",
        "16px": "size-2 [--lucide-stroke-width:0.8px]",
      },
    },
    defaultVariants: {
      size: "24px",
    },
  },
);

// ============================================
// 컴포넌트
// ============================================

const ChipIcon = forwardRef<HTMLDivElement, ChipIconProps>(
  ({ size = "24px", className, children }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(chipIconContainerVariants({ size }), className)}
      >
        <div className={chipIconInnerVariants({ size })}>{children}</div>
      </div>
    );
  },
);

ChipIcon.displayName = "ChipIcon";

export default ChipIcon;
